import { ChangeDetectionStrategy, Component, HostBinding } from '@angular/core';
import { isNotNullOrUndefined, taggedNamespace } from '@mri-platform/shared/core';
import { AuthService } from '@mri/angular-wfe-proxy-oidc';
import { combineLatest, map, Observable, shareReplay, startWith } from 'rxjs';
import { AskAgoraFrameMenuItem, isAskAgoraFrameComponent, MenuItem } from '../models';
import { getCurrentClientId$, MainNavService, WidgetService } from '../services';

interface ComponentState {
  askAiFrameMenuItem: AskAgoraFrameMenuItem | null;
  activeItem: MenuItem | null;
  clientId: string | null;
  isNavExpanded: boolean;
}

interface Projections {
  isAskAiFrameOpened: boolean;
  askAiFrameBaseUrl: string;
}

type ViewModel = ComponentState & Projections;

@Component({
  selector: 'mri-shared-widget-drawer',
  template: `
    @if (vm$ | async; as vm) {
      <ask-ai-frame
        [showIcon]="false"
        [clientId]="vm.clientId"
        [baseUrl]="vm.askAiFrameBaseUrl"
        [frameSources]="vm.askAiFrameMenuItem?.frameSources"
        [widgetOffset]="vm.isNavExpanded ? 220 : 60"
        [isActivated]="vm.isAskAiFrameOpened"
        (popupClose)="onAskAgoraFrameClosed(vm.activeItem)"
      ></ask-ai-frame>
    }
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetDrawerComponent {
  @HostBinding('attr.data-testid') testId = 'UiWidgetDrawerComponent';

  vm$: Observable<ViewModel>;

  constructor(
    private readonly navService: MainNavService,
    readonly widgetService: WidgetService,
    private readonly authService: AuthService
  ) {
    const tag = taggedNamespace(this.testId);

    const clientId$ = getCurrentClientId$(this.authService, this.navService);
    const activeItem$ = this.navService.activeItem$;
    const askAiFrameMenuItem$ = this.navService.menuItems$.pipe(
      map(menuItems => (menuItems.find(isAskAgoraFrameComponent) as AskAgoraFrameMenuItem) || null)
    );
    const isNavExpanded$ = this.navService.isExpanded$;

    // Create projections (calculations from ComponentState)...
    const isAskAiFrameOpened$ = combineLatest({
      activeItem: activeItem$,
      askAiFrameMenuItem: askAiFrameMenuItem$
    }).pipe(map(({ activeItem, askAiFrameMenuItem }) => !!askAiFrameMenuItem && activeItem === askAiFrameMenuItem));

    const askAiFrameBaseUrl$ = askAiFrameMenuItem$.pipe(
      isNotNullOrUndefined(),
      map(askAiFrameMenuItem =>
        askAiFrameMenuItem.ddChatEnabled ? askAiFrameMenuItem.ddChatBaseUrl : askAiFrameMenuItem.embedAppBaseUrl
      ),
      startWith('')
    );

    // Create ViewModel combining all state and projections
    this.vm$ = combineLatest({
      askAiFrameMenuItem: askAiFrameMenuItem$,
      activeItem: activeItem$,
      clientId: clientId$,
      isNavExpanded: isNavExpanded$,
      isAskAiFrameOpened: isAskAiFrameOpened$,
      askAiFrameBaseUrl: askAiFrameBaseUrl$
    }).pipe(tag('vm'), shareReplay({ refCount: true, bufferSize: 1 }));
  }

  onAskAgoraFrameClosed(activeItem: MenuItem | null) {
    if (activeItem && isAskAgoraFrameComponent(activeItem)) {
      this.navService.activate(activeItem);
    }
  }
}
